import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { copy } from '../copy'
import { useAuthStore } from '../store/auth'
import { useStreamStore } from '../store/stream'
import { useToastStore } from '../store/toast'
import type { HubEvent, WsDbDegradedMessage, WsInitMessage } from '../types/api'
import {
  type QueryClientLike,
  routeHubEvent,
  type StreamStore,
} from '../ws/eventRouter'

// ── Init-snapshot seeding ───────────────────────────────────────────────────

/**
 * Seed caches and connection-level state from a WS `init` snapshot.
 *
 * Runs on every connect and reconnect (the server treats `init` as the
 * authoritative snapshot), so it first *replaces* the previous presence
 * state rather than merging into it. Pure with respect to the passed
 * store/query-client — no socket knowledge — so it is unit-testable
 * without a connection.
 */
export function applyInitSnapshot(
  msg: WsInitMessage,
  queryClient: QueryClientLike,
  store: StreamStore,
): void {
  // NOTE: msg.agents (live hub actors, {agent_id, name}) is NOT
  // seeded into agentQueryKeys.workspace(...) — those entries hold
  // full AgentDefInfo[] from the workspace API, and this payload
  // carries neither workspace ids nor def fields to build them.
  const topicsByWorkspace = new Map<string, typeof msg.topics>()
  for (const topic of msg.topics) {
    const group = topicsByWorkspace.get(topic.workspace_id) ?? []
    group.push(topic)
    topicsByWorkspace.set(topic.workspace_id, group)
  }
  for (const [workspaceId, topics] of topicsByWorkspace) {
    queryClient.setQueryData(['topics', workspaceId], topics)
  }
  // A turn that persisted between this client's last history fetch and the
  // moment this subscription went live published a `message_complete` we can
  // never receive — the refresh race where the newest reply silently vanishes
  // from the transcript (and, if the initial GET was still in flight, its
  // pre-persist snapshot would install as fresh data). Init landing means
  // we're now subscribed, so refetch the thread views; cancelRefetch (the
  // query-v5 default) restarts any such in-flight fetch. Also heals
  // completions missed while a previous connection was down.
  void queryClient.invalidateQueries({ queryKey: ['thread'] })
  void queryClient.invalidateQueries({ queryKey: ['threads'] })
  // Seed the working indicator from the server's snapshot of
  // in-flight turns, so a reconnecting or newly-open client shows it
  // immediately rather than waiting for the next turn_started.
  // clearAllStreaming() ran on the preceding close (reconnect), so
  // this rebuilds presence from scratch.
  for (const topic of msg.topics) {
    for (const turn of topic.active_turns ?? []) {
      store.setTurnActive(topic.topic_id, turn.thread_id, turn.agent_id, true)
    }
  }
  // Seed connection-level presence from the init snapshot: live agent
  // ids (steady-green sidebar dots) and per-workspace online users
  // (People dots). A fresh init on reconnect repopulates both.
  store.setLiveAgents(msg.agents.map((a) => a.agent_id))
  const onlineByWs = new Map<string, string[]>()
  for (const p of msg.online_users ?? []) {
    const ids = onlineByWs.get(p.workspace_id) ?? []
    ids.push(p.user_id)
    onlineByWs.set(p.workspace_id, ids)
  }
  store.clearLiveUsers()
  for (const [wsId, ids] of onlineByWs) {
    store.setLiveUsers(wsId, ids)
  }
}

// ── Reconnecting socket ─────────────────────────────────────────────────────

export type WsStatus = 'connecting' | 'connected' | 'reconnecting'

const MAX_BACKOFF_MS = 30_000
const INITIAL_BACKOFF_MS = 1_000

/**
 * Cold-connect event-replay window. A fresh page load has no last-event
 * timestamp, so without this the server replays nothing: a turn completing
 * (or content already streamed for a still-active turn) between the page's
 * initial HTTP fetches and the socket going live would be missed entirely.
 * Replaying a short window rebuilds in-flight turn content and redelivers
 * recent `message_complete`s; completed turns replay their deltas and then
 * their completion (FIFO eviction drops deltas first), so nothing lingers.
 * Tolerates server/client clock skew up to the window size — beyond that it
 * degrades to the old behavior (healed by the init invalidation above).
 */
const COLD_CONNECT_REPLAY_WINDOW_MS = 30_000

type WsInboundMessage =
  | WsInitMessage
  | WsDbDegradedMessage
  | (HubEvent & { _ts?: number; _replay?: boolean })

/** Minimal socket surface the wrapper needs — matches the DOM WebSocket. */
export interface WsLike {
  close(): void
  onopen: (() => void) | null
  onmessage: ((e: { data: unknown }) => void) | null
  onerror: (() => void) | null
  onclose: (() => void) | null
}

interface ReconnectingSocketOptions {
  /**
   * Build the WS URL for one connection attempt. Receives the last seen
   * event timestamp (`null` on a cold connect with nothing to replay) and
   * returns `null` to skip the attempt entirely (e.g. no auth token yet) —
   * matching the original hook's no-token behavior of not connecting.
   */
  buildUrl: (sinceMs: number | null) => string | null
  onStatus: (status: WsStatus) => void
  /** `init` received: apply the snapshot, then the wrapper marks connected. */
  onInit: (msg: WsInitMessage) => void
  /** Server reported degraded setup queries; data may be incomplete. */
  onDbDegraded: () => void
  /** One hub event (everything that is not init/db_degraded). */
  onEvent: (event: HubEvent & { _ts?: number; _replay?: boolean }) => void
  /**
   * The connection dropped after having connected at least once. Callers
   * clear in-flight streaming + presence state here; the wrapper handles
   * status + rescheduling.
   */
  onDisconnect: () => void
  /** Injectable for tests; defaults to the DOM WebSocket. */
  socketFactory?: (url: string) => WsLike
}

/**
 * Owns one WebSocket's lifecycle: connect, message demux (init /
 * db_degraded / hub events), exponential-backoff reconnect, and teardown.
 *
 * Behavior preserved verbatim from the original standalone module:
 * - `status` flips to `connected` only when an `init` message lands (not
 *   on socket open), and every drop surfaces as `reconnecting`.
 * - reconnects replay missed events via `?since=<last event ts>`.
 * - teardown nukes handlers before `close()` so a superseded socket can
 *   never fire `onclose` into a dead effect (phantom reconnect loops).
 */
export class ReconnectingSocket {
  private readonly opts: ReconnectingSocketOptions
  private readonly socketFactory: (url: string) => WsLike
  private ws: WsLike | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private backoffMs = INITIAL_BACKOFF_MS
  private hasConnected = false
  private lastEventTs = 0
  private cancelled = false

  constructor(opts: ReconnectingSocketOptions) {
    this.opts = opts
    // The DOM WebSocket's handler signatures (MessageEvent etc.) are wider
    // than the minimal WsLike surface tests need, so the default factory
    // adapts it with a single localized cast.
    this.socketFactory =
      opts.socketFactory ??
      ((url: string) => new WebSocket(url) as unknown as WsLike)
  }

  /** Start the first connection attempt. */
  connect(): void {
    if (this.cancelled) return
    const url = this.opts.buildUrl(this.sinceParam())
    if (url === null) return

    const ws = this.socketFactory(url)
    this.ws = ws

    ws.onmessage = (e) => {
      if (this.cancelled) return
      try {
        const msg = JSON.parse(String(e.data)) as WsInboundMessage
        // Keep track of the latest event timestamp for reconnect replay.
        const msgTs = (msg as unknown as Record<string, unknown>)._ts
        this.lastEventTs = typeof msgTs === 'number' ? msgTs : Date.now()

        if ('type' in msg && msg.type === 'db_degraded') {
          this.opts.onDbDegraded()
          return
        }
        if ('type' in msg && msg.type === 'init') {
          this.opts.onInit(msg)
          this.hasConnected = true
          this.backoffMs = INITIAL_BACKOFF_MS
          this.opts.onStatus('connected')
          return
        }
        this.opts.onEvent(msg as HubEvent & { _ts?: number; _replay?: boolean })
      } catch (err) {
        console.warn('[ws] dropped malformed message', err)
      }
    }

    ws.onclose = () => {
      // destroy() already ran — do not touch state or schedule a reconnect
      // for a torn-down wrapper.
      if (this.cancelled) return
      // Surface every drop — including a never-completed initial connect —
      // as `reconnecting` so the banner tells the user we're trying, rather
      // than leaving a silent `connecting` state that looks healthy while no
      // events are flowing.
      this.opts.onStatus('reconnecting')
      if (this.hasConnected) {
        this.opts.onDisconnect()
      }
      this.scheduleReconnect()
    }

    ws.onerror = () => {
      if (this.cancelled) return
      ws.close()
    }
  }

  /** Tear down permanently: stop timers, neuter handlers, close the socket. */
  destroy(): void {
    this.cancelled = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      const socket = this.ws
      this.ws = null
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket.close()
    }
  }

  /** `null` until a first event/init has been seen after a real connect. */
  private sinceParam(): number | null {
    return this.hasConnected && this.lastEventTs > 0 ? this.lastEventTs : null
  }

  private scheduleReconnect(): void {
    if (this.cancelled) return
    this.reconnectTimer = setTimeout(() => {
      const next = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS)
      if (next === MAX_BACKOFF_MS && this.backoffMs !== MAX_BACKOFF_MS) {
        useToastStore.getState().addToast(copy.net.connectionLost, 'info')
      }
      this.backoffMs = next
      this.connect()
    }, this.backoffMs)
  }
}

// ── The hook ────────────────────────────────────────────────────────────────

/**
 * Single app WebSocket connection. The hook is deliberately thin — auth-token
 * wiring and React-Query/store glue. Connection mechanics live in
 * `ReconnectingSocket` above, event → store/query routing in
 * `ws/eventRouter.ts`, and init-snapshot seeding in `applyInitSnapshot`.
 */
export function useWebSocket() {
  const queryClient = useQueryClient()
  // Always holds the latest token — updated by a zustand subscription so that
  // reconnects after a token refresh use the new token rather than a stale closure value.
  const tokenRef = useRef(useAuthStore.getState().token)
  const [status, setStatus] = useState<WsStatus>('connecting')
  const [degraded, setDegraded] = useState(false)

  useEffect(() => {
    const unsub = useAuthStore.subscribe((s) => {
      tokenRef.current = s.token
    })
    return unsub
  }, [])

  useEffect(() => {
    const socket = new ReconnectingSocket({
      buildUrl: (sinceMs) => {
        const token = tokenRef.current
        if (!token) return null
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
        // Cold connect: replay the recent window instead of nothing (see
        // COLD_CONNECT_REPLAY_WINDOW_MS). Reconnects keep the exact
        // last-seen-event timestamp.
        const since =
          sinceMs !== null
            ? sinceMs
            : Date.now() - COLD_CONNECT_REPLAY_WINDOW_MS
        return `${proto}://${window.location.host}/ws?token=${token}&since=${since}`
      },
      onStatus: (next) => {
        setStatus(next)
        // A clean init payload means setup queries succeeded this time.
        if (next === 'connected') setDegraded(false)
      },
      onDbDegraded: () => setDegraded(true),
      onInit: (msg: WsInitMessage) => {
        applyInitSnapshot(msg, queryClient, useStreamStore.getState())
      },
      onEvent: (event: HubEvent & { _ts?: number; _replay?: boolean }) => {
        routeHubEvent(event, {
          store: useStreamStore.getState(),
          queryClient,
          ts: Date.now(),
        })
      },
      onDisconnect: () => {
        // Clear any streaming state that was in-flight when we lost the
        // connection — it won't be completed and would otherwise stick.
        // Preserve activity log, known persistent sub-agents, and ephemeral
        // parent mappings so the UI doesn't lose history on reconnect.
        useStreamStore.getState().clearAllStreaming()
        // Presence is connection-level state, not turn state: grey the dots
        // while reconnecting. The fresh init on reconnect repopulates both
        // live agent ids and per-workspace online users.
        useStreamStore.getState().setLiveAgents([])
        useStreamStore.getState().clearLiveUsers()
      },
    })

    socket.connect()
    return () => socket.destroy()
  }, [queryClient])

  return { status, degraded }
}
